import React, { useState, useEffect, useCallback } from "react";
import Layout from "../components/layout";
import { useDispatch } from "react-redux";
import axios from "axios";
import { showLoading, hideLoading } from "../redux/alertsSlice";
import {
  Table, Row, Col, Input, Button, Modal, Dropdown,
  Tag, Avatar, Typography, Flex, Card
} from "antd";
import moment from "moment";
import {
  SearchOutlined, ClearOutlined, ReloadOutlined,
  CalendarOutlined, PrinterOutlined, CloseOutlined,
  EyeOutlined, DownOutlined, ClockCircleOutlined,
  CheckCircleOutlined, CloseCircleOutlined, SyncOutlined,
  EnvironmentOutlined, MedicineBoxOutlined,
  DollarOutlined, FileTextOutlined
} from "@ant-design/icons";

const { Title, Text } = Typography;

/* ─── Tokens ─────────────────────────────────────────────────── */
const EMERALD    = "#0d9488";
const TEAL       = "#14b8a6";
const TEAL_LIGHT = "#ccfbf1";
const SLATE      = "#0f172a";
const SLATE_MID  = "#334155";
const MUTED      = "#94a3b8";
const SURFACE    = "#f8fafc";
const WHITE      = "#ffffff";

/* ─── Status config ──────────────────────────────────────────── */
const STATUS_CFG = {
  pending:     { color: "#f59e0b", bg: "#fef3c7", label: "Pending",   icon: <ClockCircleOutlined /> },
  approved:    { color: "#3b82f6", bg: "#dbeafe", label: "Approved",  icon: <CheckCircleOutlined /> },
  "follow-up": { color: "#8b5cf6", bg: "#ede9fe", label: "Follow-up", icon: <SyncOutlined /> },
  completed:   { color: "#10b981", bg: "#d1fae5", label: "Completed", icon: <CheckCircleOutlined /> },
  rejected:    { color: "#ef4444", bg: "#fee2e2", label: "Rejected",  icon: <CloseCircleOutlined /> },
  canceled:    { color: "#94a3b8", bg: "#f1f5f9", label: "Canceled",  icon: <CloseCircleOutlined /> },
};
const getStatusCfg = (s) => STATUS_CFG[s?.toLowerCase()] || { color: MUTED, bg: SURFACE, label: s, icon: null };

/* ─── Style injection ────────────────────────────────────────── */
const injectStyles = () => {
  if (document.getElementById("appt-user-styles")) return;
  const el = document.createElement("style");
  el.id = "appt-user-styles";
  el.textContent = `
    @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;600;700;800&family=DM+Sans:ital,wght@0,300;0,400;0,500;1,300&display=swap');

    .au-root { font-family: 'DM Sans', sans-serif; background: ${SURFACE}; min-height: 100vh; }
    .au-wrap { max-width: 1300px; margin: 0 auto; padding: 32px 24px 64px; }

    .au-hero {
      background: linear-gradient(135deg, ${SLATE} 0%, ${SLATE_MID} 100%);
      border-radius: 24px; padding: 36px 44px;
      position: relative; overflow: hidden; margin-bottom: 32px;
    }
    .au-hero::before {
      content: ''; position: absolute; top: -80px; right: -80px;
      width: 300px; height: 300px; border-radius: 50%;
      background: radial-gradient(circle, ${TEAL}44 0%, transparent 70%);
      filter: blur(32px); pointer-events: none;
    }
    .au-hero::after {
      content: ''; position: absolute; bottom: -60px; left: -60px;
      width: 200px; height: 200px; border-radius: 50%;
      background: radial-gradient(circle, ${EMERALD}33 0%, transparent 70%);
      filter: blur(26px); pointer-events: none;
    }

    .au-eyebrow {
      display: inline-block; padding: 4px 12px; border-radius: 20px;
      background: ${TEAL_LIGHT}; color: ${EMERALD};
      font-size: 11px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase;
      font-family: 'Sora', sans-serif; margin-bottom: 12px;
    }

    .au-controls {
      background: ${WHITE}; border-radius: 20px;
      box-shadow: 0 4px 20px rgba(0,0,0,.06);
      padding: 18px 24px; margin-bottom: 20px;
    }

    .au-input .ant-input-affix-wrapper {
      border-radius: 12px !important; height: 42px !important;
      border: 1.5px solid #e2e8f0 !important;
      transition: border-color .2s, box-shadow .2s !important;
    }
    .au-input .ant-input-affix-wrapper:hover,
    .au-input .ant-input-affix-wrapper-focused {
      border-color: ${TEAL} !important; box-shadow: 0 0 0 3px ${TEAL}22 !important;
    }

    .au-btn-ghost {
      border-radius: 10px !important; height: 42px !important;
      border: 1.5px solid #e2e8f0 !important;
      font-weight: 600 !important; color: ${SLATE_MID} !important;
      transition: border-color .2s, color .2s !important;
    }
    .au-btn-ghost:hover { border-color: ${TEAL} !important; color: ${EMERALD} !important; }

    .au-table-card {
      border-radius: 20px !important; border: none !important;
      box-shadow: 0 4px 20px rgba(0,0,0,.06) !important; overflow: hidden;
    }
    .au-table-card .ant-card-head {
      border-bottom: 1px solid #f1f5f9 !important;
      padding: 16px 24px !important; min-height: unset !important;
    }
    .au-table-card .ant-card-head-title {
      font-family: 'Sora', sans-serif !important;
      font-size: 15px !important; color: ${SLATE} !important; font-weight: 700 !important;
    }
    .au-table-card .ant-table-thead > tr > th {
      background: ${SURFACE} !important; color: ${MUTED} !important;
      font-size: 11px !important; font-weight: 700 !important;
      letter-spacing: 1px !important; text-transform: uppercase !important;
      border-bottom: 1px solid #f1f5f9 !important;
    }
    .au-table-card .ant-table-tbody > tr > td { border-bottom: 1px solid #f8fafc !important; }
    .au-table-card .ant-table-tbody > tr:hover > td { background: #f0fdfb !important; }

    .au-btn-view {
      border-radius: 8px !important; height: 34px !important; font-size: 12px !important;
      font-weight: 600 !important; border: 1.5px solid #e2e8f0 !important; color: ${SLATE_MID} !important;
      transition: all .2s !important;
    }
    .au-btn-view:hover { border-color: ${TEAL} !important; color: ${EMERALD} !important; }

    .au-btn-cal {
      border-radius: 8px !important; height: 34px !important; font-size: 12px !important;
      font-weight: 600 !important;
      background: linear-gradient(135deg, ${EMERALD}, ${TEAL}) !important;
      border: none !important; color: ${WHITE} !important;
      transition: transform .2s, box-shadow .2s !important;
    }
    .au-btn-cal:hover { transform: translateY(-1px) !important; box-shadow: 0 6px 14px ${TEAL}44 !important; }

    .au-summary { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 16px; }
    .au-summary-pill {
      display: inline-flex; align-items: center; gap: 5px;
      padding: 4px 12px; border-radius: 20px; font-size: 12px; font-weight: 600;
    }

    /* Modal detail rows */
    .au-detail-row {
      display: flex; align-items: center; gap: 12px; padding: 12px 16px;
      border-bottom: 1px solid #f1f5f9;
    }
    .au-detail-row:last-child { border-bottom: none; }
    .au-detail-icon {
      width: 34px; height: 34px; border-radius: 9px; flex-shrink: 0;
      background: ${TEAL_LIGHT}; color: ${EMERALD};
      display: flex; align-items: center; justify-content: center; font-size: 15px;
    }
    .au-detail-label {
      font-size: 10px; font-weight: 700; color: ${MUTED};
      text-transform: uppercase; letter-spacing: .8px; margin-bottom: 2px;
    }
    .au-detail-val { font-size: 13px; color: ${SLATE}; font-weight: 500; }

    /* Doctor strip */
    .au-doc-strip {
      display: flex; align-items: center; gap: 14px;
      padding: 14px 16px; border-radius: 14px;
      background: ${SURFACE}; border: 1px solid #e2e8f0; margin-bottom: 14px;
    }

    @keyframes fadeUp {
      from { opacity: 0; transform: translateY(16px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    .au-fade   { animation: fadeUp .5s ease both; }
    .au-fade-1 { animation-delay: .05s; }
    .au-fade-2 { animation-delay: .12s; }
  `;
  document.head.appendChild(el);
};

/* ─── ICS generator — uses schedule_date + appointmentTime ──── */
const generateICSFile = (record) => {
  if (!record?.doctorInfo) return "";
  const [startTime, endTime] = record.doctorInfo.appointmentTime || ["09:00", "09:30"];
  // ✅ schedule_date is the actual appointment date
  const baseDate  = moment(record.schedule_date || record.date).format("YYYY-MM-DD");
  const startDT   = moment(`${baseDate} ${startTime}`, "YYYY-MM-DD HH:mm").utc().format("YYYYMMDDTHHmmss[Z]");
  const endDT     = moment(`${baseDate} ${endTime}`,   "YYYY-MM-DD HH:mm").utc().format("YYYYMMDDTHHmmss[Z]");
  const now       = moment().utc().format("YYYYMMDDTHHmmss[Z]");
  const d = record.doctorInfo;
  return [
    "BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//HealthApp//EN",
    "CALSCALE:GREGORIAN", "METHOD:PUBLISH", "BEGIN:VEVENT",
    `UID:${record._id}@healthapp`, `DTSTAMP:${now}`,
    `DTSTART:${startDT}`, `DTEND:${endDT}`,
    `SUMMARY:Appointment with Dr. ${d.firstName} ${d.lastName}`,
    `DESCRIPTION:Service: ${d.service}\\nStatus: ${record.status}\\nFee: ₦${d.feePerConsultation}`,
    `LOCATION:${d.location || "Not specified"}`,
    "END:VEVENT", "END:VCALENDAR",
  ].join("\r\n");
};

/* ─── Print — opens clean styled window ─────────────────────── */
const printAppointment = (record) => {
  if (!record) return;
  const d   = record.doctorInfo;
  const cfg = STATUS_CFG[record.status?.toLowerCase()] || { label: record.status, color: "#94a3b8", bg: "#f1f5f9" };

  const rows = [
    ["Service",          d.service],
    ["Location",         d.location],
    ["Scheduled Date",   record.schedule_date ? moment(record.schedule_date).format("dddd, MMMM D YYYY") : "Not set"],
    ["Appointment Time", Array.isArray(d.appointmentTime) ? `${d.appointmentTime[0]} – ${d.appointmentTime[1]}` : "—"],
    ["Follow-up Date",   record.status === "follow-up" && record.followUpDate ? moment(record.followUpDate).format("dddd, MMMM D YYYY") : "—"],
    ["Consultation Fee", `₦${d.feePerConsultation}`],
    ["Status",           cfg.label],
  ];

  const win = window.open("", "_blank");
  win.document.write(`<!DOCTYPE html><html><head><title>Appointment</title>
  <style>
    *{box-sizing:border-box;margin:0;padding:0}
    body{font-family:Arial,sans-serif;padding:36px;color:#0f172a}
    .hdr{display:flex;align-items:center;justify-content:space-between;border-bottom:3px solid #0d9488;padding-bottom:14px;margin-bottom:20px}
    .hdr-title{font-size:22px;font-weight:800}
    .badge{padding:5px 14px;border-radius:20px;font-size:12px;font-weight:700;background:${cfg.bg};color:${cfg.color}}
    .doc{display:flex;align-items:center;gap:14px;background:#f8fafc;border:1px solid #e2e8f0;border-radius:12px;padding:14px 18px;margin-bottom:20px}
    .av{width:48px;height:48px;border-radius:50%;background:#0d9488;color:#fff;font-size:20px;font-weight:700;display:flex;align-items:center;justify-content:center;flex-shrink:0}
    .doc-name{font-size:16px;font-weight:700}
    .doc-sub{font-size:12px;color:#94a3b8;margin-top:3px}
    table{width:100%;border-collapse:collapse}
    td{padding:11px 14px;border-bottom:1px solid #f1f5f9;vertical-align:middle}
    td:first-child{font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.8px;color:#94a3b8;width:180px}
    td:last-child{font-size:14px;font-weight:500;color:#0f172a}
    .footer{margin-top:28px;font-size:11px;color:#94a3b8;text-align:center;border-top:1px solid #f1f5f9;padding-top:12px}
  </style></head>
  <body>
    <div class="hdr">
      <span class="hdr-title">Appointment Details</span>
      <span class="badge">${cfg.label}</span>
    </div>
    <div class="doc">
      <div class="av">${d.firstName?.[0] || "D"}</div>
      <div>
        <div class="doc-name">Dr. ${d.firstName} ${d.lastName}</div>
        <div class="doc-sub">${d.specialization || d.service}</div>
      </div>
    </div>
    <table><tbody>
      ${rows.map(([label, val]) => `<tr><td>${label}</td><td>${val}</td></tr>`).join("")}
    </tbody></table>
    <div class="footer">Printed on ${moment().format("MMMM D YYYY, h:mm A")}</div>
  </body></html>`);
  win.document.close();
  win.onload = () => { win.print(); win.close(); };
};

/* ─── Component ──────────────────────────────────────────────── */
function Appointment() {
  const [appointments, setAppointments]               = useState([]);
  const [filteredInfo, setFilteredInfo]               = useState({});
  const [sortedInfo, setSortedInfo]                   = useState({});
  const [searchTerm, setSearchTerm]                   = useState("");
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [isModalVisible, setIsModalVisible]           = useState(false);
  const dispatch = useDispatch();

  useEffect(() => { injectStyles(); }, []);

  const getAppointmentData = useCallback(async () => {
    try {
      dispatch(showLoading());
      const res = await axios.get("/api/user/get-appointments-by-user-id", {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      dispatch(hideLoading());
      if (res.data.success) setAppointments(res.data.data);
    } catch (e) { dispatch(hideLoading()); console.error(e); }
  }, [dispatch]);

  useEffect(() => { getAppointmentData(); }, [getAppointmentData]);

  const handleChange = (_, filters, sorter) => { setFilteredInfo(filters); setSortedInfo(sorter); };
  const clearFilters = () => setFilteredInfo({});
  const clearAll     = () => { setFilteredInfo({}); setSortedInfo({}); setSearchTerm(""); };

  const filteredAppointments = appointments.filter(a =>
    a.doctorInfo.service.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const statusCounts = filteredAppointments.reduce((acc, a) => {
    acc[a.status] = (acc[a.status] || 0) + 1;
    return acc;
  }, {});

  /* ── Calendar menu — uses schedule_date + appointmentTime ── */
  const buildCalMenu = (record) => {
    const d = record.doctorInfo;
    const [startTime, endTime] = d.appointmentTime || ["09:00", "09:30"];

    // ✅ KEY FIX: use schedule_date (the actual booked day), not record.date (submission date)
    const baseDate    = moment(record.schedule_date || record.date).format("YYYY-MM-DD");
    const startMoment = moment(`${baseDate} ${startTime}`, "YYYY-MM-DD HH:mm");
    const endMoment   = moment(`${baseDate} ${endTime}`,   "YYYY-MM-DD HH:mm");

    const title    = `Appointment – ${d.service}`;
    const details  = `Doctor: Dr. ${d.firstName} ${d.lastName}\nService: ${d.service}\nStatus: ${record.status}\nFee: ₦${d.feePerConsultation}`;
    const location = d.location || "";

    const googleUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE`
      + `&text=${encodeURIComponent(title)}`
      + `&details=${encodeURIComponent(details)}`
      + `&location=${encodeURIComponent(location)}`
      + `&dates=${startMoment.utc().format("YYYYMMDDTHHmmss[Z]")}/${endMoment.utc().format("YYYYMMDDTHHmmss[Z]")}`;

    const outlookUrl = `https://outlook.live.com/calendar/0/deeplink/compose`
      + `?subject=${encodeURIComponent(title)}`
      + `&body=${encodeURIComponent(details)}`
      + `&location=${encodeURIComponent(location)}`
      + `&startdt=${encodeURIComponent(startMoment.toISOString())}`
      + `&enddt=${encodeURIComponent(endMoment.toISOString())}`;

    const downloadICS = () => {
      const ics  = generateICSFile(record);
      const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href = url; a.download = `appointment-${record._id}.ics`; a.click();
      URL.revokeObjectURL(url);
    };

    return {
      items: [
        { key: "google",  label: <span><CalendarOutlined style={{ marginRight: 6 }} />Google Calendar</span> },
        { key: "outlook", label: <span><CalendarOutlined style={{ marginRight: 6 }} />Outlook / Microsoft 365</span> },
        { key: "ics",     label: <span><CalendarOutlined style={{ marginRight: 6 }} />Download .ics (Apple / other)</span> },
      ],
      onClick: ({ key }) => {
        if (key === "google")  window.open(googleUrl, "_blank");
        if (key === "outlook") window.open(outlookUrl, "_blank");
        if (key === "ics")     downloadICS();
      },
    };
  };

  /* ── Modal detail rows ── */
  const getDetailRows = (record) => {
    if (!record) return [];
    const d = record.doctorInfo;
    return [
      { icon: <MedicineBoxOutlined />, label: "Service",          val: d.service },
      { icon: <EnvironmentOutlined />, label: "Location",         val: d.location },
      { icon: <CalendarOutlined />,    label: "Scheduled Date",   val: record.schedule_date ? moment(record.schedule_date).format("dddd, MMMM D YYYY") : "Not set" },
      { icon: <ClockCircleOutlined />, label: "Appointment Time", val: Array.isArray(d.appointmentTime) ? `${d.appointmentTime[0]} – ${d.appointmentTime[1]}` : "—" },
      ...(record.status === "follow-up" ? [{ icon: <SyncOutlined />, label: "Follow-up Date", val: record.followUpDate ? moment(record.followUpDate).format("dddd, MMMM D YYYY") : "—" }] : []),
      { icon: <DollarOutlined />,      label: "Consultation Fee", val: `₦${d.feePerConsultation}` },
    ];
  };

  /* ── Table columns ── */
  const columns = [
    {
      title: "Doctor", key: "doctorName", width: 190,
      sorter: (a, b) => a.doctorInfo.firstName.localeCompare(b.doctorInfo.firstName),
      sortOrder: sortedInfo.columnKey === "doctorName" ? sortedInfo.order : null,
      render: (_, r) => (
        <Flex align="center" gap={10}>
          <Avatar size={34} style={{ background: `linear-gradient(135deg, ${EMERALD}, ${TEAL})`, fontSize: 13, flexShrink: 0 }}>
            {r.doctorInfo.firstName?.[0]}
          </Avatar>
          <div style={{ minWidth: 0 }}>
            <Text strong style={{ fontSize: 13, display: "block", color: SLATE, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              Dr. {r.doctorInfo.firstName} {r.doctorInfo.lastName}
            </Text>
            <Text style={{ fontSize: 11, color: MUTED }}>{r.doctorInfo.specialization || r.doctorInfo.service}</Text>
          </div>
        </Flex>
      ),
    },
    {
      title: "Service", key: "service", width: 130, ellipsis: true,
      filters: [...new Set(appointments.map(a => a.doctorInfo.service))].map(s => ({ text: s, value: s })),
      filteredValue: filteredInfo.service || null,
      onFilter: (v, r) => r.doctorInfo.service === v,
      sorter: (a, b) => a.doctorInfo.service.localeCompare(b.doctorInfo.service),
      sortOrder: sortedInfo.columnKey === "service" ? sortedInfo.order : null,
      render: (_, r) => (
        <Flex align="center" gap={5}>
          <MedicineBoxOutlined style={{ color: EMERALD, fontSize: 11 }} />
          <Text style={{ fontSize: 13 }}>{r.doctorInfo.service}</Text>
        </Flex>
      ),
    },
    {
      title: "Location", key: "location", width: 130, ellipsis: true, responsive: ["md"],
      sorter: (a, b) => (a.doctorInfo.location || "").localeCompare(b.doctorInfo.location || ""),
      sortOrder: sortedInfo.columnKey === "location" ? sortedInfo.order : null,
      render: (_, r) => (
        <Flex align="center" gap={5}>
          <EnvironmentOutlined style={{ color: MUTED, fontSize: 11 }} />
          <Text style={{ fontSize: 13, color: SLATE_MID }}>{r.doctorInfo.location}</Text>
        </Flex>
      ),
    },
    {
      title: "Fee", key: "fee", width: 90,
      sorter: (a, b) => a.doctorInfo.feePerConsultation - b.doctorInfo.feePerConsultation,
      sortOrder: sortedInfo.columnKey === "fee" ? sortedInfo.order : null,
      render: (_, r) => <Text style={{ fontSize: 13, fontWeight: 600, color: SLATE }}>₦{r.doctorInfo.feePerConsultation}</Text>,
    },
    {
      title: "Scheduled Date", key: "schedule_date", width: 135,
      sorter: (a, b) => moment(a.schedule_date || 0).unix() - moment(b.schedule_date || 0).unix(),
      sortOrder: sortedInfo.columnKey === "schedule_date" ? sortedInfo.order : null,
      render: (_, r) => {
        const has = (r.status === "approved" || r.status === "follow-up") && r.schedule_date;
        return has
          ? <Text style={{ fontSize: 13, color: SLATE_MID, fontWeight: 500 }}>{moment(r.schedule_date).format("MMM D, YYYY")}</Text>
          : <Text style={{ color: MUTED, fontSize: 12 }}>—</Text>;
      },
    },
    {
      title: "Appt. Time", key: "appointmentTime", width: 120,
      render: (_, r) => {
        const time = r.doctorInfo?.appointmentTime;
        const ok   = r.status === "approved" || r.status === "follow-up";
        return ok && Array.isArray(time) && time.length === 2
          ? <Text style={{ fontSize: 13, color: SLATE_MID }}>{time[0]} – {time[1]}</Text>
          : <Text style={{ color: MUTED, fontSize: 12 }}>—</Text>;
      },
    },
    {
      title: "Follow-up Date", key: "followUpDate", width: 135,
      sorter: (a, b) => moment(a.followUpDate || 0).unix() - moment(b.followUpDate || 0).unix(),
      sortOrder: sortedInfo.columnKey === "followUpDate" ? sortedInfo.order : null,
      render: (_, r) => r.status === "follow-up" && r.followUpDate
        ? <Text style={{ fontSize: 13, color: "#8b5cf6", fontWeight: 500 }}>{moment(r.followUpDate).format("MMM D, YYYY")}</Text>
        : <Text style={{ color: MUTED, fontSize: 12 }}>—</Text>,
    },
    {
      title: "Status", key: "status", width: 125,
      filters: [
        { text: "Approved",  value: "approved" },
        { text: "Pending",   value: "pending" },
        { text: "Rejected",  value: "rejected" },
        { text: "Follow-Up", value: "follow-up" },
        { text: "Completed", value: "completed" },
      ],
      filteredValue: filteredInfo.status || null,
      onFilter: (v, r) => r.status === v,
      render: (_, r) => {
        const cfg = getStatusCfg(r.status);
        return (
          <Tag style={{ background: cfg.bg, color: cfg.color, border: "none", borderRadius: 20, fontWeight: 600, fontSize: 11, display: "inline-flex", alignItems: "center", gap: 4 }}>
            {cfg.icon}{cfg.label}
          </Tag>
        );
      },
    },
    {
      title: "Actions", key: "actions", width: 185, fixed: "right",
      render: (_, record) => {
        const isApproved = record.status === "approved" || record.status === "follow-up";
        return (
          <Flex gap={6} align="center">
            <Button className="au-btn-view" icon={<EyeOutlined />}
              onClick={() => { setSelectedAppointment(record); setIsModalVisible(true); }}>
              View
            </Button>
            {isApproved && (
              <Dropdown trigger={["click"]} menu={buildCalMenu(record)}>
                <Button className="au-btn-cal" icon={<CalendarOutlined />}>
                  Export <DownOutlined style={{ fontSize: 9 }} />
                </Button>
              </Dropdown>
            )}
          </Flex>
        );
      },
    },
  ];

  return (
    <Layout>
      <div className="au-root">
        <div className="au-wrap">

          {/* Hero */}
          <div className="au-hero au-fade">
            <div style={{ position: "relative", zIndex: 1 }}>
              <div className="au-eyebrow"><CalendarOutlined style={{ marginRight: 5 }} />My Appointments</div>
              <Title level={2} style={{ margin: "6px 0 4px", fontFamily: "'Sora', sans-serif", color: WHITE, fontWeight: 800 }}>
                Your Appointment History
              </Title>
              <Text style={{ color: MUTED, fontSize: 14 }}>
                Track, manage, and export all your booked appointments
              </Text>
            </div>
          </div>

          {/* Controls */}
          <div className="au-controls au-fade au-fade-1">
            <Row gutter={[14, 14]} align="middle">
              <Col xs={24} sm={14} md={10}>
                <div className="au-input">
                  <Input
                    prefix={<SearchOutlined style={{ color: MUTED }} />}
                    placeholder="Search by service…"
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    allowClear
                  />
                </div>
              </Col>
              <Col xs={24} sm={10} md={14}>
                <Flex gap={8} justify="flex-end">
                  <Button className="au-btn-ghost" icon={<ClearOutlined />} onClick={clearFilters}>Clear Filters</Button>
                  <Button className="au-btn-ghost" icon={<ReloadOutlined />} onClick={clearAll}>Reset All</Button>
                </Flex>
              </Col>
            </Row>
          </div>

          {/* Status summary pills */}
          {filteredAppointments.length > 0 && (
            <div className="au-summary au-fade au-fade-1">
              {Object.entries(statusCounts).map(([status, count]) => {
                const cfg = getStatusCfg(status);
                return (
                  <span key={status} className="au-summary-pill" style={{ background: cfg.bg, color: cfg.color }}>
                    {cfg.icon}&nbsp;{cfg.label}: {count}
                  </span>
                );
              })}
            </div>
          )}

          {/* Table */}
          <div className="au-fade au-fade-2">
            <Card
              className="au-table-card"
              title="All Appointments"
              bodyStyle={{ padding: 0 }}
              extra={
                <Tag style={{ background: TEAL_LIGHT, color: EMERALD, border: "none", borderRadius: 20, fontWeight: 700 }}>
                  {filteredAppointments.length} total
                </Tag>
              }
            >
              <Table
                columns={columns}
                dataSource={filteredAppointments.map(a => ({ ...a, key: a._id }))}
                rowKey="_id"
                onChange={handleChange}
                scroll={{ x: "max-content" }}
                pagination={{
                  pageSize: 10, showSizeChanger: false,
                  showTotal: (total, range) => `Showing ${range[0]}–${range[1]} of ${total}`,
                  style: { padding: "16px 24px" },
                }}
                size="middle"
              />
            </Card>
          </div>
        </div>
      </div>

      {/* ── Detail Modal ── */}
      <Modal
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        width={500}
        centered
        footer={
          <Flex gap={8} justify="flex-end" style={{ paddingTop: 4 }}>
            <Button
              icon={<PrinterOutlined />}
              onClick={() => printAppointment(selectedAppointment)}
              style={{ borderRadius: 10, height: 40, fontWeight: 600, border: `1.5px solid ${TEAL}`, color: EMERALD }}
            >Print</Button>
            <Button
              icon={<CloseOutlined />}
              onClick={() => setIsModalVisible(false)}
              style={{ borderRadius: 10, height: 40, fontWeight: 600, border: "1.5px solid #e2e8f0", color: SLATE_MID }}
            >Close</Button>
          </Flex>
        }
        title={
          <Flex align="center" gap={10}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: TEAL_LIGHT, display: "flex", alignItems: "center", justifyContent: "center", color: EMERALD, flexShrink: 0 }}>
              <FileTextOutlined />
            </div>
            <Text strong style={{ fontSize: 15, color: SLATE }}>Appointment Details</Text>
          </Flex>
        }
      >
        {selectedAppointment && (() => {
          const d   = selectedAppointment.doctorInfo;
          const cfg = getStatusCfg(selectedAppointment.status);
          return (
            <div style={{ paddingTop: 8 }}>
              {/* Doctor strip */}
              <div className="au-doc-strip">
                <Avatar size={48} style={{ background: `linear-gradient(135deg, ${EMERALD}, ${TEAL})`, fontSize: 20, fontWeight: 700, flexShrink: 0 }}>
                  {d.firstName?.[0]}
                </Avatar>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Text strong style={{ fontSize: 14, display: "block", color: SLATE }}>
                    Dr. {d.firstName} {d.lastName}
                  </Text>
                  <Text style={{ color: MUTED, fontSize: 12 }}>{d.specialization || d.service}</Text>
                </div>
                <Tag style={{ background: cfg.bg, color: cfg.color, border: "none", borderRadius: 20, fontWeight: 700, fontSize: 11, display: "inline-flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
                  {cfg.icon}&nbsp;{cfg.label}
                </Tag>
              </div>

              {/* Detail rows in a bordered container */}
              <div style={{ borderRadius: 12, border: "1px solid #f1f5f9", overflow: "hidden" }}>
                {getDetailRows(selectedAppointment).map((row, i) => (
                  <div key={i} className="au-detail-row">
                    <div className="au-detail-icon">{row.icon}</div>
                    <div>
                      <div className="au-detail-label">{row.label}</div>
                      <div className="au-detail-val">{row.val}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}
      </Modal>
    </Layout>
  );
}

export default Appointment;